import { Injectable } from '@angular/core';
import { SharedJobService } from '../components/client/shared/services/shared-job.service';
import { SharedClientService } from '../components/client/shared/services/shared-client.service';
import { AuthService } from './auth.service';
import { URLSearchParams } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';

interface PosthogConfig {
	api_host: string;
	ui_host: string;
	defaults: string;
	disable_surveys: boolean;
	disable_web_experiments: boolean;
	advanced_disable_feature_flags: boolean;
	loaded: () => void;
}

interface Posthog {
	init: (token: string, config: PosthogConfig) => void;
	identify: (distinctId: string, personProperties?: Object) => void;
	register: (properties: Object) => void;
	reset: () => void;
	get_distinct_id: () => string;
	get_property: (property: string) => any;
}


export class PosthogService {
	constructor(
		private sharedJobService: SharedJobService,
		private sharedClientService: SharedClientService,
		private authService: AuthService) {
			this.init();
	}

	get posthog() {
		return (window as any).posthog as Posthog || null;
	}

	public init() {
		if (!process.env.POSTHOG_PUBLIC_KEY || !this.posthog) {
			return;
		}

		this.posthog.init(process.env.POSTHOG_PUBLIC_KEY, {
			api_host: process.env.POSTHOG_PUBLIC_API_HOST,
			// Required alongside a proxied api_host: the SDK otherwise derives the app URL from it.
			ui_host: process.env.POSTHOG_PUBLIC_UI_HOST,
			defaults: '2025-05-24',
			// This app is frozen, so a survey must never appear in it, and flags and experiments
			// live in GrowthBook. Surveys otherwise poll every second while the tab is visible.
			disable_surveys: true,
			disable_web_experiments: true,
			advanced_disable_feature_flags: true,
			// The head stub queues writes but returns undefined from the getters identify() reads.
			loaded: () => this.identify()
		});
	}

	public logout() {
		if (!this.posthog) {
			return;
		}

		this.posthog.reset();
	}

	// Only clients are identified; everyone else still gets pageviews and autocapture.
	private identify() {
		if (this.authService.userType !== 'client') {
			return;
		}

		let params: URLSearchParams = new URLSearchParams();
		params.set('status', 'active');
		params.set('external', '0');
		Observable.forkJoin(
			this.sharedJobService.getClientJobs(this.authService.userId, params),
			this.sharedClientService.get(this.authService.userId)
		).subscribe(
			([jobs, client]) => {
				this.resetForeignIdentity(client.uuid);
				this.applyGlobalEventProps(client);
				this.applyPersonProps(client, jobs);
			}
		);
	}

	// An anonymous id is left alone so its pre-login events merge into the identified person.
	private resetForeignIdentity(uuid: string) {
		const distinctId = this.posthog.get_distinct_id();
		// distinct_id equals the generated $device_id while anonymous, and differs once identified
		const wasIdentified = !!distinctId && distinctId !== this.posthog.get_property('$device_id');

		if (wasIdentified && distinctId !== uuid) {
			this.posthog.reset();
		}
	}

	private applyPersonProps(client: any, jobs: any[]) {
		const props: Record<string, any> = {
			user_type: 'client',
			name: client.name,
			status: client.status,
			is_master_account: client.is_master_account,
			number_of_jobs: jobs ? jobs.length : 0,
			customer_success_manager: client.owner
		};
		if (!client.is_master_account) {
			props['master_account_id'] = client.master_account_id;
		}

		this.posthog.identify(client.uuid, props);
	}

	private applyGlobalEventProps(client: any) {
		const props: Record<string, any> = {
			user_type: 'client',
			status: client.status,
			is_impersonation: this.authService.isUs
		};
		if (this.authService.adminId) {
			props['impersonator_id'] = this.authService.adminId;
		}

		this.posthog.register(props);
	}
static ctorParameters() { return [ { type: SharedJobService }, { type: SharedClientService }, { type: AuthService } ]; }}



// WEBPACK FOOTER //
// ./node_modules/ngc-webpack??ref--0-2!./node_modules/angular2-template-loader!./src/app/providers/posthog.service.ts